import { formatGenericEmailHref } from 'components/util';
import { getTheme, ITheme, Link } from '@fluentui/react';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { trackException } from 'services';

interface ErrorBoundaryProps extends Partial<RouteComponentProps> {
  renderError?: (error: Error) => JSX.Element;
}

interface ErrorBoundaryState {
  error?: Error;
  /**
   * Callback to stop listening to history changes
   */
  unlistenHistory?: () => void;
  theme?: ITheme;
}

const componentName = 'ErrorBoundary';

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    const theme = getTheme();
    super(props);
    this.state = {
      error: undefined,
      unlistenHistory: undefined,
      theme: theme
    };
  }

  static getDerivedStateFromError(error: Error): Record<string, Error> {
    // Update state so the next render will show the fallback UI.
    return { error: error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    if (!error.stack) {
      error.stack = info.componentStack;
    }

    error.message = `${componentName}: ${error.message}`;
    trackException(error);
  }

  // On mount, add a listener to detect changes in the history
  // If URL changes, disable the error page and continue
  componentDidMount(): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const unlistenHistory = this?.props?.history?.listen((_location, _action) => {
      if (this.state.error) {
        this.setState({ error: undefined });
      }
    });

    this.setState({ unlistenHistory: unlistenHistory });
  }

  componentWillUnmount(): void {
    if (this.state.unlistenHistory) {
      this.state.unlistenHistory();

      this.setState({ unlistenHistory: undefined });
    }
  }

  render(): React.ReactNode {
    if (this.state.error) {
      if (this.props.renderError) {
        return this.props.renderError(this.state.error);
      } else {
        return (
          <div className="align-self-center" style={{ margin: 100, fontSize: 48, fontWeight: 24, lineHeight: 1.2 }}>
            {'Sorry, the Salus Moderation Platform is having problems. Refresh the page or check back soon.'}
            <br />
            <br />
            <br />
            <Link
              className="pr-4"
              href={formatGenericEmailHref()}
              style={{ color: this.state.theme?.palette.themePrimary }}
              tabIndex={41}
            >
              {'Report a bug'}
            </Link>
          </div>
        );
      }
    }

    return this.props.children;
  }
}
